import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  List,
  ListItem,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  MetaPredictions,
  MetaPredictionsData,
  MetaPredictionsResponse,
} from '../../../../../api/services/mediaPlanning/getMetaPredictionsValidity';
import PricingAccordions from '../PricingAccordions/PricingAccordions';

export type GenerateReportDialogProps = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onSecondaryAction?: () => void;
  header: string;
  text?: string | null | React.ReactNode;
  isLoading?: boolean;
  isInformationalOnly?: boolean;
  cancelText?: string | null;
  secondaryActionText?: string | null;
  proceedText?: string | null;
  isValidityCheck?: boolean;
  isBooking?: boolean;
  pricingData?: MetaPredictions;
  type?: string;
};

function MetaBookingDialog({
  open,
  onClose,
  onSuccess,
  onSecondaryAction,
  header,
  isLoading,
  text,
  isInformationalOnly = false,
  cancelText = 'Cancel',
  proceedText = 'OK',
  secondaryActionText,
  isValidityCheck = false,
  isBooking = false,
  pricingData,
  type = 'information',
}: GenerateReportDialogProps) {
  const { t } = useTranslation();
  const theme = useTheme();

  const getBackgroundColor = () => {
    if (!(isLoading && isValidityCheck)) {
      return type === 'information' ? '#0183E8' : '#E5484D';
    }
    return undefined;
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (
          (isValidityCheck || isBooking) &&
          isLoading &&
          (reason === 'backdropClick' || reason === 'escapeKeyDown')
        ) {
          return;
        }
        onClose();
      }}
      disableEscapeKeyDown={(isValidityCheck || isBooking) && isLoading}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      PaperProps={{
        sx: { borderRadius: '10px', padding: '16px', width: '500px' },
      }}
    >
      <DialogTitle
        id="alert-dialog-title"
        style={{
          verticalAlign: 'left',
          display: 'flex',
          color: theme.palette.error.main,
        }}
      >
        <Typography
          sx={{
            margin: 0,
            alignSelf: 'self-end',
            fontSize: '28px',
            fontWeight: 700,
            color: type === 'information' ? '#447EEB' : '#F00F00',
          }}
        >
          {header}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <DialogContentText
          id="alert-dialog-description"
          sx={{ color: '#525F81', fontWeight: 400 }}
        >
          {!isLoading && (
            <Typography>
              {(!isValidityCheck ||
                (isValidityCheck &&
                  pricingData &&
                  pricingData.data.length > 0)) &&
                text}
            </Typography>
          )}
        </DialogContentText>
        {isLoading && (isValidityCheck || isBooking) && (
          <LinearProgress sx={{ my: 2 }} />
        )}
        {isLoading && (
          <Typography>
            {t('mediaPlanning.metaBooking.modals.validityCheck.text.loading')}
          </Typography>
        )}
        {!isLoading && isValidityCheck && !pricingData?.data.length && (
          <Stack>
            <Typography>
              We couldn&apos;t find any pending predictions for your campaign.
              Possible reasons:
            </Typography>
            <List component="ul" sx={{ pl: 2, listStyleType: 'disc' }}>
              <ListItem component="li" sx={{ display: 'list-item', pl: 0 }}>
                <Typography>
                  Some of your countries might not contain Meta RF.
                </Typography>
              </ListItem>
              <ListItem component="li" sx={{ display: 'list-item', pl: 0 }}>
                <Typography>
                  All Meta RFs in your plan are already booked.
                </Typography>
              </ListItem>
            </List>
            <Typography>or</Typography>
            <List component="ul" sx={{ pl: 2, listStyleType: 'disc' }}>
              <ListItem component="li" sx={{ display: 'list-item', pl: 0 }}>
                <Typography>
                  Meta API outage prevented prediction creation: In this case
                  please try recalculating the budget.
                </Typography>
              </ListItem>
            </List>
          </Stack>
        )}
        {!isLoading && isValidityCheck && pricingData && (
          <PricingAccordions
            pricingData={pricingData}
            isLoading={isLoading}
            isValidityCheck={isValidityCheck}
          />
        )}
      </DialogContent>
      <DialogActions style={{ gap: '1rem' }}>
        {onSecondaryAction && (
          <Button
            onClick={onClose}
            size="large"
            variant="text"
            style={{
              backgroundColor: 'transparent',
              minWidth: '100px',
              borderRadius: 5,
              color: '#60646C',
            }}
          >
            {cancelText}
          </Button>
        )}
        {!isInformationalOnly && (
          <Button
            onClick={onSecondaryAction || onClose}
            size="large"
            variant="text"
            style={{
              backgroundColor: '#0000330F',
              minWidth: '100px',
              borderRadius: 5,
              color: '#60646C',
            }}
          >
            {secondaryActionText || cancelText}
          </Button>
        )}
        {!isBooking && (
          <Button
            onClick={() => {
              onSuccess();
              onClose();
            }}
            variant="contained"
            // color="primary"
            size="large"
            style={{
              backgroundColor: getBackgroundColor(),
              width: 'auto',
              minWidth: '100px',
              borderRadius: 5,
            }}
            disabled={isLoading && (isValidityCheck || isBooking)}
          >
            {proceedText}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default MetaBookingDialog;
